import { useCallback, useEffect, useState } from 'react';
import { connectAdminDashboardSocket } from '../services/adminSocket';
import {
  patchAdminParrainageRegle,
  patchAdminParrainageSettings,
  patchCurrencySettings,
} from '../services/academiaAdminService';
import {
  getAdminSystemSettings,
  patchAdminSystemLegal,
  patchAdminSystemPlatform,
  postAdminPlatformLogo,
} from '../services/adminService';
import { invalidatePublicBranding } from './usePublicBranding';
import type { AdminParrainageRegle, AdminSystemCurrencySettings, AdminSystemSettingsData } from '../types/admin';
import { normalizePlatformSocialLinks } from '../config/platformSocialNetworks';

interface UseSystemSettingsUser {
  id?: string;
  role?: string;
}

export type ParrainageRegleDraft = {
  seuilFilleuls: string;
  tauxCommissionPct: string;
};

export function useSystemSettings(user?: UseSystemSettingsUser) {
  const [data, setData] = useState<AdminSystemSettingsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setIsError(false);
      setSaveError(null);
      const payload = await getAdminSystemSettings();
      const normalized = {
        ...payload,
        platform: {
          ...payload.platform,
          supportPhone: payload.platform?.supportPhone ?? null,
          socialLinks: normalizePlatformSocialLinks(payload.platform?.socialLinks),
        },
        currency: payload.currency as AdminSystemCurrencySettings,
        parrainage: payload.parrainage ?? { bonusInscriptionCdf: 0, regles: [] },
      };
      setData(normalized as AdminSystemSettingsData);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.id || user.role !== 'ADMINISTRATEUR') {
      setIsLoading(false);
      return undefined;
    }
    void load();
    return undefined;
  }, [load, user?.id, user?.role]);

  useEffect(() => {
    if (!user?.id || user.role !== 'ADMINISTRATEUR') {
      return undefined;
    }
    return connectAdminDashboardSocket({
      userId: user.id,
      role: user.role,
      onRefresh: (payload) => {
        if (!payload?.scope || payload.scope === 'all') {
          void load();
        }
      },
    });
  }, [load, user?.id, user?.role]);

  const saveApplicationSettings = useCallback(
    async (form: {
      platform: AdminSystemSettingsData['platform'];
      cguContent: string;
      confidentialiteContent: string;
      initial: AdminSystemSettingsData;
      currency: AdminSystemCurrencySettings;
      parrainageBonus: string;
      parrainageRegleDrafts: Record<string, ParrainageRegleDraft>;
      recalculateTarifs: boolean;
    }) => {
      setIsSaving(true);
      setSaveError(null);
      try {
        await patchAdminSystemPlatform({
          appName: form.platform.appName,
          slogan: form.platform.slogan,
          supportEmail: form.platform.supportEmail,
          supportPhone: form.platform.supportPhone,
          socialLinks: form.platform.socialLinks,
          logoUrl: form.platform.logoUrl,
          publicSiteUrl: form.platform.publicSiteUrl ?? null,
          maintenanceMode: form.platform.maintenanceMode,
          pushNotifications: form.platform.pushNotifications,
          soundAlerts: form.platform.soundAlerts,
          autoDiagnostics: form.platform.autoDiagnostics,
        });

        if (form.cguContent !== form.initial.legal.cgu.content) {
          await patchAdminSystemLegal('CGU', form.cguContent);
        }
        if (form.confidentialiteContent !== form.initial.legal.confidentialite.content) {
          await patchAdminSystemLegal('CONFIDENTIALITE', form.confidentialiteContent);
        }

        const initialCurrency = form.initial.currency;
        const currencyDirty =
          form.currency.displayMode !== initialCurrency.displayMode ||
          Number(form.currency.usdToCdfRate) !== Number(initialCurrency.usdToCdfRate) ||
          Number(form.currency.cdfToUsdRate) !== Number(initialCurrency.cdfToUsdRate);

        if (currencyDirty) {
          await patchCurrencySettings({
            displayMode: form.currency.displayMode,
            usdToCdfRate: Number(form.currency.usdToCdfRate),
            cdfToUsdRate: Number(form.currency.cdfToUsdRate),
            recalculateTarifs: form.recalculateTarifs,
          });
        }

        const bonusValue = Number(form.parrainageBonus);
        const snapshotBonus = Number(form.initial.parrainage.bonusInscriptionCdf ?? 0);
        // Bonus inscription forcé à 0 (désactivé) — synchronise la base si un ancien montant reste.
        if (snapshotBonus !== 0 || (Number.isFinite(bonusValue) && bonusValue !== 0)) {
          await patchAdminParrainageSettings({ bonusInscriptionCdf: 0 });
        }

        for (const regle of form.initial.parrainage.regles) {
          const draft = form.parrainageRegleDrafts[regle.id] ?? {
            seuilFilleuls: String(regle.seuilFilleuls),
            tauxCommissionPct: String(regle.tauxCommissionPct),
          };
          const seuil = Number(draft.seuilFilleuls);
          const taux = Number(draft.tauxCommissionPct);
          const seuilChanged = Number.isFinite(seuil) && seuil !== regle.seuilFilleuls;
          const tauxChanged = Number.isFinite(taux) && taux !== regle.tauxCommissionPct;
          if (seuilChanged || tauxChanged) {
            if (!Number.isFinite(seuil) || seuil < 1) {
              throw new Error(`Seuil de filleuls invalide pour ${regle.typeProfilLabel}.`);
            }
            if (!Number.isFinite(taux) || taux < 0 || taux > 100) {
              throw new Error(`Taux de commission invalide pour ${regle.typeProfilLabel}.`);
            }
            await patchAdminParrainageRegle(regle.id, {
              seuilFilleuls: seuil,
              tauxCommissionPct: taux,
            });
          }
        }

        await load();
        invalidatePublicBranding();
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : 'Enregistrement impossible');
        throw e;
      } finally {
        setIsSaving(false);
      }
    },
    [load],
  );

  const uploadPlatformLogo = useCallback(
    async (file: File) => {
      setIsUploadingLogo(true);
      setSaveError(null);
      try {
        await postAdminPlatformLogo(file);
        await load();
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : 'Téléversement impossible');
        throw e;
      } finally {
        setIsUploadingLogo(false);
      }
    },
    [load],
  );

  const clearPlatformLogo = useCallback(async () => {
    setIsUploadingLogo(true);
    setSaveError(null);
    try {
      await patchAdminSystemPlatform({ logoUrl: null });
      await load();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Suppression impossible');
      throw e;
    } finally {
      setIsUploadingLogo(false);
    }
  }, [load]);

  const savePlatformAndLegal = useCallback(
    async (form: {
      platform: AdminSystemSettingsData['platform'];
      cguContent: string;
      confidentialiteContent: string;
      initial: AdminSystemSettingsData;
    }) => {
      await saveApplicationSettings({
        ...form,
        currency: form.initial.currency,
        parrainageBonus: String(form.initial.parrainage.bonusInscriptionCdf ?? 0),
        parrainageRegleDrafts: buildParrainageRegleDrafts(form.initial.parrainage.regles),
        recalculateTarifs: false,
      });
    },
    [saveApplicationSettings],
  );

  return {
    data,
    isLoading,
    isError,
    isSaving,
    isUploadingLogo,
    saveError,
    refetch: load,
    saveApplicationSettings,
    savePlatformAndLegal,
    uploadPlatformLogo,
    clearPlatformLogo,
  };
}

export function buildParrainageRegleDrafts(regles: AdminParrainageRegle[]): Record<string, ParrainageRegleDraft> {
  return Object.fromEntries(
    regles.map((regle) => [
      regle.id,
      {
        seuilFilleuls: String(regle.seuilFilleuls),
        tauxCommissionPct: String(regle.tauxCommissionPct),
      },
    ]),
  );
}
